import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { collection, doc, getDoc, updateDoc, arrayRemove, arrayUnion, deleteDoc, getDocs, query, where, limit } from 'firebase/firestore';
import { Users, UserX, Crown, AlertCircle, Loader2, Trash2, ArrowLeft, Copy, Check, Key, UserPlus, Search } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useSubscriptionLimits } from '../hooks/useSubscription';
import { UpgradePrompt } from '../components/UpgradePrompt';
import { CustomCategoriesManager } from '../components/CustomCategoriesManager';

export function HouseholdSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [householdData, setHouseholdData] = useState<any>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<any>(null);
  const subscription = useSubscriptionLimits();

  useEffect(() => {
    if (user) {
      loadHouseholdData();
    }
  }, [user]);

  async function loadHouseholdData() {
    try {
      setLoading(true);
      setError('');

      // Get user's household
      const userDoc = await getDoc(doc(db, 'users', user!.uid));
      const userData = userDoc.data();
      
      if (!userData?.householdId) {
        setError('No household found');
        setLoading(false);
        return;
      }

      // Get household details
      const householdDoc = await getDoc(doc(db, 'households', userData.householdId));
      if (!householdDoc.exists()) {
        setError('Household not found');
        setLoading(false);
        return;
      }

      const household = householdDoc.data();
      setHouseholdData({ id: householdDoc.id, ...household });

      // Get member details
      const memberPromises = household.members.map(async (memberId: string) => {
        const memberDoc = await getDoc(doc(db, 'users', memberId));
        return {
          id: memberId,
          ...memberDoc.data(),
          isOwner: memberId === household.ownerId
        };
      });

      const memberData = await Promise.all(memberPromises);
      setMembers(memberData);
      setLoading(false);
    } catch (err: any) {
      console.error('Error loading household:', err);
      setError('Failed to load household data');
      setLoading(false);
    }
  }

  async function handleCopyJoinCode() {
    if (householdData?.joinCode) {
      try {
        await navigator.clipboard.writeText(householdData.joinCode);
        setCodeCopied(true);
        setTimeout(() => setCodeCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy code:', err);
      }
    }
  }

  async function handleSearchUser(e: React.FormEvent) {
    e.preventDefault();

    try {
      setSearching(true);
      setError('');
      setSearchResult(null);

      // Check subscription limits
      if (!subscription.canAddMember) {
        setSearching(false);
        setShowUpgradePrompt(true);
        return;
      }

      // Search for user by email
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', searchEmail.toLowerCase()), limit(1));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError(`No user found with email: ${searchEmail}`);
        setSearching(false);
        return;
      }

      const userDoc = querySnapshot.docs[0];
      const userData = { id: userDoc.id, ...userDoc.data() } as any;

      // Check if user is already a member
      if (householdData.members?.includes(userData.id)) {
        setError('This user is already a member of your household');
        setSearching(false);
        return;
      }

      // Check if user is in another household
      if (userData.householdId && userData.householdId !== householdData.id) {
        setError('This user is already in another household. They must leave it first.');
        setSearching(false);
        return;
      }

      setSearchResult(userData);
      setSearching(false);
    } catch (err: any) {
      console.error('Error searching user:', err);
      setError('Failed to search for user');
      setSearching(false);
    }
  }

  async function handleAddUser() {
    if (!searchResult) return;

    try {
      setSearching(true);
      setError('');

      // Add user to household
      await updateDoc(doc(db, 'households', householdData.id), {
        members: arrayUnion(searchResult.id)
      });

      // Update user's household reference
      await updateDoc(doc(db, 'users', searchResult.id), {
        householdId: householdData.id
      });

      setSuccess(`${searchResult.email} added to household!`);
      setSearchEmail('');
      setSearchResult(null);
      setSearching(false);
      loadHouseholdData();
    } catch (err: any) {
      console.error('Error adding user:', err);
      setError('Failed to add user to household');
      setSearching(false);
    }
  }

  async function handleRemoveMember(memberId: string) {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }

    try {
      setError('');
      
      // Remove member from household
      await updateDoc(doc(db, 'households', householdData.id), {
        members: arrayRemove(memberId)
      });

      // Remove household from user
      await updateDoc(doc(db, 'users', memberId), {
        householdId: null
      });

      setSuccess('Member removed successfully');
      loadHouseholdData();
    } catch (err: any) {
      console.error('Error removing member:', err);
      setError('Failed to remove member');
    }
  }

  async function handleLeaveHousehold() {
    if (!window.confirm('Are you sure you want to leave this household?')) {
      return;
    }

    try {
      setError('');
      
      // Remove self from household
      await updateDoc(doc(db, 'households', householdData.id), {
        members: arrayRemove(user!.uid)
      });

      // Remove household from user
      await updateDoc(doc(db, 'users', user!.uid), {
        householdId: null
      });

      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      console.error('Error leaving household:', err);
      setError('Failed to leave household');
    }
  }

  async function handleDeleteHousehold() {
    if (!window.confirm('⚠️ WARNING: This will permanently delete the household and remove all members. This cannot be undone!')) {
      return;
    }

    if (!window.confirm('Are you ABSOLUTELY sure? Type DELETE in the next prompt to confirm.')) {
      return;
    }

    const confirmation = window.prompt('Type DELETE to confirm:');
    if (confirmation !== 'DELETE') {
      setError('Deletion cancelled - confirmation text did not match');
      return;
    }

    try {
      setError('');
      setLoading(true);

      // Remove household from all members
      for (const member of members) {
        await updateDoc(doc(db, 'users', member.id), {
          householdId: null
        });
      }

      // Delete all food items in this household
      const foodItemsQuery = query(
        collection(db, 'items'),
        where('householdId', '==', householdData.id)
      );
      const foodItems = await getDocs(foodItemsQuery);
      for (const item of foodItems.docs) {
        await deleteDoc(item.ref);
      }

      // Delete all invites for this household
      const invitesQuery = query(
        collection(db, 'invites'),
        where('householdId', '==', householdData.id)
      );
      const invites = await getDocs(invitesQuery);
      for (const invite of invites.docs) {
        await deleteDoc(invite.ref);
      }

      // Delete the household
      await deleteDoc(doc(db, 'households', householdData.id));

      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      console.error('Error deleting household:', err);
      setError('Failed to delete household');
      setLoading(false);
    }
  }

  const isOwner = householdData?.ownerId === user?.uid;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20 flex items-center justify-center px-4 py-6 overflow-x-hidden">
        <div className="text-center w-full max-w-md">
          <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 text-primary-green animate-spin mx-auto mb-4" />
          <p className="text-sm sm:text-base text-gray-600 font-medium">Loading household settings...</p>
        </div>
      </div>
    );
  }

  if (error === 'No household found' || error === 'Household not found') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20 overflow-x-hidden">
        <div className="w-full max-w-full sm:max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="mb-6 sm:mb-8 w-full">
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="flex items-center gap-2 text-gray-600 hover:text-primary-green transition-colors mb-4 group text-sm sm:text-base"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-medium">Back to Dashboard</span>
            </button>
            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 break-words">Household Settings</h1>
            <p className="text-xs sm:text-sm md:text-base text-gray-600 break-words">Manage your household members and invitations</p>
          </div>

          <div className="bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg p-4 sm:p-6 border border-gray-100 text-center overflow-hidden">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <AlertCircle className="w-6 h-6 sm:w-7 sm:h-7 text-red-600" />
            </div>
            <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2 sm:mb-3">No Household Found</h2>
            <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-4 sm:mb-6 break-words">
              Your account is not currently linked to a household. This might have happened if:
            </p>
            <ul className="text-left text-xs sm:text-sm md:text-base text-gray-600 mb-4 sm:mb-6 space-y-1 sm:space-y-2 max-w-md mx-auto">
              <li className="break-words">• Your household was deleted</li>
              <li className="break-words">• You were removed from a household</li>
              <li className="break-words">• There was an error during account setup</li>
            </ul>
            <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 overflow-hidden">
              <p className="text-xs sm:text-sm text-blue-900 break-words">
                <strong>To fix this:</strong> Contact support or have an administrator recreate your household in the Firebase Console using the household recreation guide.
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="inline-block bg-primary-green text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold hover:bg-primary-green/90 transition-all text-sm sm:text-base"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20">
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Back Button & Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard', { replace: true })}
            className="flex items-center gap-2 text-gray-600 hover:text-primary-green transition-colors mb-4 group"
          >
            <ArrowLeft className="w-6 h-6 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to Dashboard</span>
          </button>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Household Settings</h1>
          <p className="text-gray-600">Manage your household members and invitations</p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 mb-6">
            <p className="text-sm">✓ {success}</p>
          </div>
        )}

        <div className="space-y-6">
          {/* Members List */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-primary-green" />
              <h2 className="text-lg font-bold text-gray-900">Household Members</h2>
              <span className="bg-primary-green text-white px-3 py-1 rounded-full text-sm font-semibold">
                {members.length}
              </span>
            </div>

            <div className="space-y-3">
              {members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 flex-shrink-0 bg-primary-mint rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary-green" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <p className="font-semibold text-gray-900 truncate">
                          {member.displayName || member.email}
                        </p>
                        {member.isOwner && (
                          <span className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                            <Crown className="w-3 h-3" />
                            Owner
                          </span>
                        )}
                        {member.id === user?.uid && (
                          <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                            You
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">{member.email}</p>
                    </div>
                  </div>

                  {isOwner && member.id !== user?.uid && (
                    <button
                      onClick={() => handleRemoveMember(member.id)}
                      className="flex-shrink-0 text-red-600 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove member"
                    >
                      <UserX className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Join Code */}
          <div className="bg-gradient-to-br from-primary-green to-primary-mint rounded-2xl shadow-lg p-6 border border-primary-green/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                <Key className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-white">Quick Join Code</h2>
                <p className="text-sm text-white/90">Share for instant access</p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white/80 mb-1 font-medium">Join Code</p>
                  <p className="text-xl font-bold text-white tracking-wide font-mono break-all">
                    {householdData?.joinCode || 'Loading...'}
                  </p>
                </div>
                <button
                  onClick={handleCopyJoinCode}
                  className="flex-shrink-0 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-xl transition-all hover:scale-105 active:scale-95"
                  title="Copy code"
                >
                  {codeCopied ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
              {codeCopied && (
                <p className="text-sm text-white/90 font-medium animate-fade-in">
                  ✓ Code copied to clipboard!
                </p>
              )}
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-sm text-white/90 mb-2 font-medium">How to use:</p>
              <ol className="text-sm text-white/80 space-y-1">
                <li>1. Share this code with family members</li>
                <li>2. They go to <Link to="/join-household" className="font-semibold text-white underline hover:text-white/80">Join Household page</Link></li>
                <li>3. Enter code → Instant access!</li>
              </ol>
            </div>
          </div>

          {/* Search & Add Existing User */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <Search className="w-6 h-6 text-primary-green" />
              <h2 className="text-lg font-bold text-gray-900">Add Existing User</h2>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Instantly add someone who already has a FridgeLife account
            </p>

            <form onSubmit={handleSearchUser} className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  placeholder="Search by email address"
                  required
                  className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-green focus:border-primary-green transition-all"
                  disabled={searching}
                />
                <button
                  type="submit"
                  disabled={searching || !searchEmail}
                  className="px-6 py-3 bg-primary-green text-white rounded-xl font-semibold hover:bg-primary-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {searching ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Searching...</span>
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5" />
                      <span>Search User</span>
                    </>
                  )}
                </button>
              </div>

              {/* Search Result */}
              {searchResult && (
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <UserPlus className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 truncate">
                          {searchResult.displayName || searchResult.email}
                        </p>
                        <p className="text-sm text-gray-600 truncate">{searchResult.email}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddUser}
                      disabled={searching}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 transition-all whitespace-nowrap sm:flex-shrink-0 w-full sm:w-auto"
                    >
                      Add to Household
                    </button>
                  </div>
                </div>
              )}
            </form>

            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-3">
              <p className="text-sm text-blue-900">
                <strong>Note:</strong> This searches for users who already have a FridgeLife account. If they don't have an account yet, they can create one and join using your household's join code.
              </p>
            </div>
          </div>

          {/* Custom Categories */}
          {householdData && (
            <div className="mb-6">
              <CustomCategoriesManager householdId={householdData.id} />
            </div>
          )}

          {/* Danger Zone */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-red-200">
            <h2 className="text-lg font-bold text-red-600 mb-4">Danger Zone</h2>

            {isOwner ? (
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Crown className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-yellow-900 mb-1">You're the household owner</p>
                      <p className="text-sm text-yellow-800">
                        As the owner, you cannot leave the household. You can either delete the household permanently or transfer ownership to another member first.
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleDeleteHousehold}
                  className="w-full bg-red-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-red-700 transition-all flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-5 h-5" />
                  Delete Household Permanently
                </button>
                <p className="text-xs text-gray-500 text-center">
                  This will remove all members and delete all household data
                </p>
              </div>
            ) : (
              <div>
                <p className="text-gray-600 mb-4">
                  Leave this household and remove your access to shared food items.
                </p>
                <button
                  onClick={handleLeaveHousehold}
                  className="bg-red-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-red-700 transition-all flex items-center gap-2"
                >
                  <UserX className="w-5 h-5" />
                  Leave Household
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upgrade Prompt Modal */}
      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        limitType="members"
        currentCount={subscription.currentMemberCount}
        maxCount={subscription.maxMembers}
      />
    </div>
  );
}
